import type { DocumentNode } from 'graphql'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'

/**
 * Structural subset of `@apollo/client`'s `ApolloClient` that the
 * mutation runners actually touch. Lets the runner factory accept an
 * injectable client in tests so the dispatch can be mocked without
 * standing up the full Apollo Client + link + cache stack. The real
 * `apolloClient` satisfies this interface structurally.
 *
 * Phase B: `resolveClient` moved to `@/shared/ports/resolveClient` so
 * the other 11 feature files (Phase B G2 migration) can share the
 * canonical seam. `ApolloMutateClient` stays as a standalone structural
 * type (it carries a tighter `mutation` argument union — `DocumentNode |
 * TypedDocumentNode<T, …>` instead of `unknown` — because that's what
 * the runner's call sites pass); `resolveClient` accepts an injected
 * value satisfying this narrower shape, and returns a value of the
 * broader `ApolloClientLike` type which is structurally compatible
 * with every `.mutate` invocation the runners make.
 */
export interface ApolloMutateClient {
  mutate: <T>(options: {
    readonly mutation:
      | DocumentNode
      | TypedDocumentNode<T, Record<string, unknown>>
    readonly variables: Record<string, unknown>
  }) => Promise<{ readonly data?: T | null | undefined }>
}

// ── public interfaces ───────────────────────────────────────────────────────

export interface MutationRunnersRefs {
  readonly error: Ref<string | null>
}

export interface MutationRunnersApi {
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
  /**
   * Result-returning sibling of `run`. It reports only the mutation
   * transport outcome; state convergence is delivered by `boardChanged`'s
   * authoritative snapshot rather than a follow-up query.
   */
  readonly runReporting: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    acknowledge?: (result: T) => boolean,
  ) => Promise<boolean>
  readonly runMutate: <_T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    options?: {
      readonly onError?: () => void
    },
  ) => Promise<boolean>
}

export function useMutationRunners(
  refs: MutationRunnersRefs,
  // Optional injection point — production callers omit it and the
  // imported `apolloClient` is used. Tests pass a fake so the dispatch
  // can be exercised without a live Apollo Client. Parameter type is
  // widened to the shared helper's `ApolloClientLike | undefined` so
  // the `injected` slot stays interchangeable with what `resolveClient`
  // itself accepts; the runner only ever calls `.mutate` on it, which
  // `ApolloClientLike` covers.
  injected?: Parameters<typeof resolveClient>[0],
): MutationRunnersApi {
  // Renamed from the plan's `c` back to `client` so the legacy
  // source-level regex test
  // (`useMutationRunners exposes a result-returning runReporting sibling
  // alongside run`, in `lib/__tests__/playerTags.test.ts`) keeps matching
  // `await client.mutate` — it pins the dispatch through either
  // `client.mutate` or `apolloClient.mutate`. Once that pin test is
  // retired (Phase 6 — Phase 1 lands the port infrastructure without
  // churning it) the rename can be undone.
  const client = resolveClient(injected)
  async function run<T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ): Promise<void> {
    try {
      const res = await client.mutate<T>({ mutation: mutation as never, variables })
      if (done && res.data) done(res.data)
      refs.error.value = null
    } catch (e: unknown) {
      refs.error.value = e instanceof Error ? e.message : String(e)
    }
  }

  /**
   * Always resolves to a boolean. A `true` result means the server accepted
   * the write; the store deliberately waits for the subscription snapshot to
   * replace visible state, keeping every tab on the same post-commit view.
   */
  async function runReporting<T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    acknowledge: (result: T) => boolean = () => true,
  ): Promise<boolean> {
    try {
      const res = await client.mutate<T>({ mutation: mutation as never, variables })
      if (!res.data) {
        refs.error.value = 'Server returned an empty response. Please try again.'
        return false
      }
      if (!acknowledge(res.data)) {
        refs.error.value = 'Server did not acknowledge the requested change. Please try again.'
        return false
      }
      refs.error.value = null
      return true
    } catch (e: unknown) {
      refs.error.value = e instanceof Error ? e.message : String(e)
      return false
    }
  }

  async function runMutate<T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    options: {
      readonly onError?: () => void
    } = {},
  ): Promise<boolean> {
    try {
      await client.mutate<T>({ mutation: mutation as never, variables })
      refs.error.value = null
      return true
    } catch (e: unknown) {
      refs.error.value = e instanceof Error ? e.message : String(e)
      options.onError?.()
      return false
    }
  }

  return { run, runReporting, runMutate }
}
